import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Printer, ArrowLeft, Truck, CheckCircle, Package, Clock, Trash2, Edit2, Save, X, Plus, Minus, MessageCircle, CalendarIcon, Inbox } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";
import { QRCodeSVG } from "qrcode.react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

const LOGO_URL = "/logo.jpg";

const statusLabels = { pending: "Pending", sent_to_worker: "Sent to Worker", received_from_worker: "Received from Worker", product_ready: "Product Ready", received: "Received" };
const statusFlow = ["pending", "sent_to_worker", "received_from_worker", "product_ready", "received"];
const statusIcons = { pending: Clock, sent_to_worker: Truck, received_from_worker: Inbox, product_ready: CheckCircle, received: Package };

const SHIRT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck"];
const KURTA_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck", "Mori"];
const COAT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck", "Jacket Length", "Cross Back", "Mori"];
const PRINCE_COAT_MEASUREMENTS = ["Length", "Chest", "Waist", "Hip", "Shoulder", "Sleeves", "Biceps", "Neck", "Cross Back", "Mori"];
const LOWER_BODY_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Knee", "Calf", "Bottom", "U"];
const PANT_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Knee", "Calf", "Bottom", "U"];
const PAJAMA_MEASUREMENTS = ["Length", "Waist", "Hip", "Thigh", "Bottom", "U"];

const CLOTHING_TYPES = [
  { value: "shirt", label: "Shirt", measurements: SHIRT_MEASUREMENTS },
  { value: "coat", label: "Coat", measurements: COAT_MEASUREMENTS },
  { value: "prince_coat", label: "Prince Coat", measurements: PRINCE_COAT_MEASUREMENTS },
  { value: "sherwani", label: "Sherwani", measurements: COAT_MEASUREMENTS },
  { value: "kurta", label: "Kurta", measurements: KURTA_MEASUREMENTS },
  { value: "short_kurta", label: "Short Kurta", measurements: KURTA_MEASUREMENTS },
  { value: "pathani", label: "Pathani", measurements: KURTA_MEASUREMENTS },
  { value: "waistcoat", label: "Waistcoat", measurements: SHIRT_MEASUREMENTS },
  { value: "modi_jacket", label: "Modi Jacket", measurements: SHIRT_MEASUREMENTS },
  { value: "blazer", label: "Blazer", measurements: COAT_MEASUREMENTS },
  { value: "pant", label: "Pant", measurements: PANT_MEASUREMENTS },
  { value: "trouser", label: "Trouser", measurements: PANT_MEASUREMENTS },
  { value: "pajama", label: "Pajama", measurements: PAJAMA_MEASUREMENTS },
  { value: "shalwar", label: "Shalwar", measurements: LOWER_BODY_MEASUREMENTS },
  { value: "police_uniform", label: "Police Uniform", measurements: SHIRT_MEASUREMENTS },
];

// Get special measurements for print based on clothing type
const getSpecialPrintMeasurements = (clothingType, measurements) => {
  const type = clothingType?.toLowerCase();
  if (type === 'pant' || type === 'trouser' || type === 'shalwar') {
    return { Knee: measurements['Knee'] || '-', Calf: measurements['Calf'] || '-', U: measurements['U'] || '-' };
  }
  if (type === 'pajama') {
    return { U: measurements['U'] || '-' };
  }
  if (type === 'coat' || type === 'sherwani' || type === 'blazer') {
    return { 
      'Jacket Length': measurements['Jacket Length'] || '-', 
      'Cross Back': measurements['Cross Back'] || '-',
      'Mori': measurements['Mori'] || '-'
    };
  }
  if (type === 'prince_coat') {
    return { 
      'Cross Back': measurements['Cross Back'] || '-',
      'Mori': measurements['Mori'] || '-'
    };
  }
  if (type === 'kurta' || type === 'short_kurta' || type === 'pathani') {
    return { 'Mori': measurements['Mori'] || '-' };
  }
  return null;
};

// Get main measurements for print (excluding special ones)
const getMainPrintMeasurements = (clothingType, measurements) => {
  const type = clothingType?.toLowerCase();
  const excludeKeys = [];
  if (type === 'pant' || type === 'trouser' || type === 'shalwar') {
    excludeKeys.push('Knee', 'Calf', 'U');
  }
  if (type === 'pajama') {
    excludeKeys.push('U');
  }
  if (type === 'coat' || type === 'sherwani' || type === 'blazer') {
    excludeKeys.push('Jacket Length', 'Cross Back', 'Mori');
  }
  if (type === 'prince_coat') {
    excludeKeys.push('Cross Back', 'Mori');
  }
  if (type === 'kurta' || type === 'short_kurta' || type === 'pathani') {
    excludeKeys.push('Mori');
  }
  const filtered = {};
  Object.entries(measurements || {}).forEach(([k, v]) => {
    if (!excludeKeys.includes(k)) filtered[k] = v;
  });
  return filtered;
};

export default function SlipDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editMeasurements, setEditMeasurements] = useState({});
  const [editNotes, setEditNotes] = useState("");
  const [editQuantity, setEditQuantity] = useState(1);
  const [editWorker, setEditWorker] = useState({ id: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ clothing_type: "", quantity: 1, measurements: {}, notes: "", worker_id: "", worker_name: "" });
  const [messageOpen, setMessageOpen] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [newDeliveryDate, setNewDeliveryDate] = useState(undefined);
  const [workers, setWorkers] = useState([]);

  useEffect(() => {
    api.get(`/slips/${id}`).then(res => { setSlip(res.data); setLoading(false); })
      .catch(() => { toast.error("Slip not found"); navigate("/slips"); });
    // Fetch workers list
    api.get("/workers").then(res => setWorkers(res.data.filter(w => w.active))).catch(() => {});
  }, [id, navigate]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await api.patch(`/slips/${id}/status`, { status: newStatus });
      setSlip({ ...slip, ...res.data });
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setUpdating(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this slip permanently?")) return;
    try {
      await api.delete(`/slips/${id}`);
      toast.success("Slip deleted");
      navigate("/slips");
    } catch { toast.error("Failed to delete"); }
  };

  const handlePrint = () => window.print();

  const toggleItemReceived = async (idx) => {
    if (!slip?.items?.[idx]) return;
    const item = slip.items[idx];
    const currentlyReceived = Boolean(item.received_from_worker_at);
    try {
      const res = await api.patch(`/slips/${id}/items/${idx}`, {
        received_from_worker: !currentlyReceived,
      });
      setSlip(res.data);
      toast.success(currentlyReceived ? "Marked as pending" : "Article received from worker");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const startEditing = (idx) => {
    const item = slip.items[idx];
    setEditingIdx(idx);
    setEditMeasurements({ ...item.measurements });
    setEditNotes(item.notes || "");
    setEditQuantity(item.quantity || 1);
    setEditWorker({ id: item.worker_id || "", name: item.worker_name || "" });
  };

  const cancelEditing = () => {
    setEditingIdx(null);
    setEditMeasurements({});
    setEditNotes("");
    setEditQuantity(1);
    setEditWorker({ id: "", name: "" });
  };

  const saveEditing = async () => {
    setSaving(true);
    try {
      const updatedItems = slip.items.map((item, idx) => {
        if (idx === editingIdx) {
          return { 
            ...item, 
            measurements: editMeasurements, 
            notes: editNotes, 
            quantity: editQuantity,
            worker_id: editWorker.id || null,
            worker_name: editWorker.name || null
          };
        }
        return item;
      });
      await api.put(`/slips/${id}`, { items: updatedItems });
      const res = await api.get(`/slips/${id}`);
      setSlip(res.data);
      setEditingIdx(null);
      toast.success("Item updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const handleWorkerChange = (workerId) => {
    if (workerId === "none") {
      setEditWorker({ id: "", name: "" });
    } else {
      const worker = workers.find(w => w.id === workerId);
      setEditWorker({ id: workerId, name: worker?.name || "" });
    }
  };

  const handleNewItemWorkerChange = (workerId) => {
    if (workerId === "none") {
      setNewItem({ ...newItem, worker_id: "", worker_name: "" });
    } else {
      const worker = workers.find(w => w.id === workerId);
      setNewItem({ ...newItem, worker_id: workerId, worker_name: worker?.name || "" });
    }
  };

  const handleNewItemTypeChange = (typeValue) => {
    const clothing = CLOTHING_TYPES.find(c => c.value === typeValue);
    const keys = clothing ? clothing.measurements : [];
    const measurements = {};
    keys.forEach(k => { measurements[k] = ""; });
    setNewItem({ ...newItem, clothing_type: typeValue, measurements });
  };

  const addNewItem = async () => {
    if (!newItem.clothing_type) { toast.error("Please select clothing type"); return; }
    setSaving(true);
    try {
      const itemToAdd = {
        clothing_type: newItem.clothing_type,
        quantity: newItem.quantity,
        measurements: newItem.measurements,
        notes: newItem.notes,
        worker_id: newItem.worker_id || null,
        worker_name: newItem.worker_name || null
      };
      const updatedItems = [...slip.items, itemToAdd];
      await api.put(`/slips/${id}`, { items: updatedItems });
      const res = await api.get(`/slips/${id}`);
      setSlip(res.data);
      setShowAddItem(false);
      setNewItem({ clothing_type: "", quantity: 1, measurements: {}, notes: "", worker_id: "", worker_name: "" });
      toast.success("Item added to slip");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add item");
    }
    setSaving(false);
  };

  const deleteItem = async (idx) => {
    if (slip.items.length === 1) { toast.error("Cannot delete the only item"); return; }
    if (!window.confirm("Delete this item?")) return;
    setSaving(true);
    try {
      const updatedItems = slip.items.filter((_, i) => i !== idx);
      await api.put(`/slips/${id}`, { items: updatedItems });
      const res = await api.get(`/slips/${id}`);
      setSlip(res.data);
      toast.success("Item deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete item");
    }
    setSaving(false);
  };

  const saveDeliveryDate = async () => {
    if (!newDeliveryDate) return;
    setSaving(true);
    try {
      await api.put(`/slips/${id}`, { delivery_date: format(newDeliveryDate, "yyyy-MM-dd") });
      const res = await api.get(`/slips/${id}`);
      setSlip(res.data);
      setEditingDelivery(false);
      toast.success("Delivery date updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update delivery date");
    }
    setSaving(false);
  };

  const startEditingDelivery = () => {
    setNewDeliveryDate(slip.delivery_date ? parseISO(slip.delivery_date) : undefined);
    setEditingDelivery(true);
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground animate-pulse">Loading slip...</div>;
  if (!slip) return null;

  const currentIdx = statusFlow.indexOf(slip.status);
  const totalQuantity = slip.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;

  return (
    <div data-testid="slip-detail-page">
      {/* Screen View */}
      <div className="no-print space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in-up">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/slips")} className="h-9 w-9 text-muted-foreground">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">{slip.slip_number}</h1>
              <p className="text-muted-foreground text-sm">
                {slip.created_at ? format(parseISO(slip.created_at), "dd MMM yyyy, hh:mm a") : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint} className="rounded-sm border-border gap-2 flex-1 sm:flex-none">
              <Printer className="w-4 h-4" /> Print
            </Button>
            <Button variant="ghost" size="icon" onClick={handleDelete} className="h-9 w-9 text-red-400 hover:text-red-300">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Status Flow */}
        <Card className="luxury-card rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-serif text-base">Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {statusFlow.map((s, i) => {
                const Icon = statusIcons[s];
                const isCurrent = s === slip.status;
                const isPast = i < currentIdx;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <div className={`flex items-center gap-1 px-2 py-1.5 rounded-sm text-[10px] md:text-xs font-medium ${
                      isCurrent ? `status-${s}` : isPast ? "bg-white/[0.04] text-muted-foreground" : "bg-white/[0.02] text-muted-foreground/50"
                    }`}>
                      <Icon className="w-3 h-3" />
                      <span className="hidden sm:inline">{statusLabels[s]}</span>
                    </div>
                    {i < statusFlow.length - 1 && <div className={`w-3 h-[1px] ${isPast || isCurrent ? "bg-[#D4AF37]/50" : "bg-border"}`} />}
                  </div>
                );
              })}
            </div>
            {slip.status !== "received" && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">Update to:</span>
                <Select onValueChange={updateStatus} disabled={updating}>
                  <SelectTrigger className="w-full sm:w-48 bg-background border-border rounded-sm h-10">
                    <SelectValue placeholder="Change status" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {statusFlow.filter((_, i) => i > currentIdx).map(s => (
                      <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer & Worker */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="luxury-card rounded-sm">
            <CardHeader className="pb-3"><CardTitle className="font-serif text-base">Customer</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <p className="text-foreground font-medium">{slip.customer?.name || slip.customer_name}</p>
              <p className="text-sm text-muted-foreground">{slip.customer?.phone || slip.customer_phone}</p>
            </CardContent>
          </Card>
          <Card className="luxury-card rounded-sm">
            <CardHeader className="pb-3"><CardTitle className="font-serif text-base">Worker & Delivery</CardTitle></CardHeader>
            <CardContent>
              {/* Per-article worker assignment + received-back toggle */}
              <div className="space-y-2 mb-3">
                {slip.items?.map((item, idx) => {
                  const received = Boolean(item.received_from_worker_at);
                  return (
                    <div key={idx} className="flex items-center justify-between gap-2 text-sm" data-testid={`article-row-${idx}`}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-muted-foreground capitalize truncate">
                          Article {idx + 1} — {item.clothing_type?.replace(/_/g, " ")}
                        </span>
                        {item.worker_name ? (
                          <Badge variant="outline" className="text-[10px] rounded-sm bg-[rgba(114,47,55,0.12)] text-[#D4AF37] border-[rgba(114,47,55,0.3)] flex-shrink-0">
                            {item.worker_name}
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-muted-foreground/60 italic flex-shrink-0">Unassigned</span>
                        )}
                      </div>
                      {item.worker_id && (
                        received ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Badge className="text-[10px] rounded-sm bg-emerald-950/40 text-emerald-300 border border-emerald-700/40" data-testid={`article-received-badge-${idx}`}>
                              <CheckCircle className="w-2.5 h-2.5 mr-1" />
                              Received
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => toggleItemReceived(idx)} className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400" data-testid={`article-unmark-btn-${idx}`}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => toggleItemReceived(idx)} className="h-7 text-[11px] px-2 rounded-sm border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[rgba(114,47,55,0.15)] flex-shrink-0" data-testid={`article-mark-received-btn-${idx}`}>
                            <Inbox className="w-3 h-3 mr-1" /> Mark Received
                          </Button>
                        )
                      )}
                    </div>
                  );
                })}
                {(!slip.items || slip.items.length === 0) && (
                  <p className="text-sm text-muted-foreground">No articles</p>
                )}
              </div>
              {editingDelivery ? (
                <div className="mt-3 space-y-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start bg-background border-border rounded-sm h-10">
                        <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                        {newDeliveryDate ? format(newDeliveryDate, "dd MMM yyyy") : <span className="text-muted-foreground">Select date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                      <Calendar mode="single" selected={newDeliveryDate} onSelect={setNewDeliveryDate} />
                    </PopoverContent>
                  </Popover>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveDeliveryDate} disabled={saving} className="btn-maroon rounded-sm h-8 text-xs flex-1">
                      {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditingDelivery(false)} className="h-8 text-xs">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1 pt-3 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Delivery: {slip.delivery_date ? format(parseISO(slip.delivery_date), "dd MMM yyyy") : "Not set"}
                  </p>
                  <Button variant="ghost" size="sm" onClick={startEditingDelivery} className="h-6 text-xs text-muted-foreground hover:text-foreground">
                    <Edit2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
              <p className="text-sm gold-text mt-2">Total Articles: {totalQuantity}</p>
            </CardContent>
          </Card>
        </div>

        {/* Article History — per-article assignment + receipt timeline */}
        {slip.items && slip.items.length > 0 && (
          <Card className="luxury-card rounded-sm" data-testid="article-history-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif text-base flex items-center gap-2">
                <Inbox className="w-4 h-4 text-[#D4AF37]" /> Article History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="py-2 px-3 sm:px-4 font-medium">Article</th>
                      <th className="py-2 px-3 sm:px-4 font-medium">Worker</th>
                      <th className="py-2 px-3 sm:px-4 font-medium">Assigned</th>
                      <th className="py-2 px-3 sm:px-4 font-medium">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slip.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-border/40 last:border-b-0" data-testid={`article-history-row-${idx}`}>
                        <td className="py-2 px-3 sm:px-4 capitalize">
                          <span className="text-muted-foreground text-xs">#{idx + 1}</span>{" "}
                          <span className="text-foreground">{item.clothing_type?.replace(/_/g, " ")}</span>
                        </td>
                        <td className="py-2 px-3 sm:px-4">
                          {item.worker_name ? (
                            <span className="text-foreground">{item.worker_name}</span>
                          ) : (
                            <span className="text-muted-foreground/60 italic text-xs">Unassigned</span>
                          )}
                        </td>
                        <td className="py-2 px-3 sm:px-4 text-xs text-muted-foreground">
                          {item.assigned_at ? format(parseISO(item.assigned_at), "dd MMM yyyy · HH:mm") : "—"}
                        </td>
                        <td className="py-2 px-3 sm:px-4 text-xs">
                          {item.received_from_worker_at ? (
                            <span className="text-emerald-300">
                              {format(parseISO(item.received_from_worker_at), "dd MMM yyyy · HH:mm")}
                              {item.received_from_worker_by_name && (
                                <span className="text-muted-foreground"> · {item.received_from_worker_by_name}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Items */}
        {slip.items?.map((item, idx) => (
          <Card key={idx} className="luxury-card rounded-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="font-serif text-base capitalize">{item.clothing_type?.replace(/_/g, " ")}</CardTitle>
                <Badge variant="outline" className="text-xs gold-text border-[#D4AF37]/30">Qty: {item.quantity || 1}</Badge>
              </div>
              {editingIdx === idx ? (
                <div className="flex items-center gap-1">
                  <Button size="sm" onClick={saveEditing} disabled={saving} className="btn-maroon rounded-sm h-8 text-xs gap-1 px-3">
                    <Save className="w-3 h-3" /> {saving ? "..." : "Save"}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={cancelEditing} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEditing(idx)} className="h-8 text-xs gap-1 text-muted-foreground hover:text-foreground">
                    <Edit2 className="w-3 h-3" /> Edit
                  </Button>
                  {slip.items.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => deleteItem(idx)} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {editingIdx === idx ? (
                <>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3">
                      <Label className="text-xs text-muted-foreground">Qty:</Label>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" onClick={() => setEditQuantity(Math.max(1, editQuantity - 1))} className="h-9 w-9 border-border">
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-10 text-center text-sm">{editQuantity}</span>
                        <Button variant="outline" size="icon" onClick={() => setEditQuantity(editQuantity + 1)} className="h-9 w-9 border-border">
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Assign Worker:</Label>
                      <Select value={editWorker.id || undefined} onValueChange={handleWorkerChange}>
                        <SelectTrigger className="flex-1 bg-background border-border rounded-sm h-9">
                          <SelectValue placeholder="Select worker" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="none">No worker</SelectItem>
                          {workers.map(w => (
                            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(editMeasurements).map(([key, val]) => (
                      <div key={key} className="space-y-1">
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{key}</label>
                        <Input
                          value={val}
                          onChange={(e) => setEditMeasurements(prev => ({ ...prev, [key]: e.target.value }))}
                          className="bg-background border-border rounded-sm h-10 text-sm"
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</label>
                    <textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      className="w-full h-16 bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground resize-none outline-none focus:border-[rgba(212,175,55,0.5)]"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Show assigned worker for this item */}
                  {item.worker_name && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Assigned to:</span>
                      <Badge variant="outline" className="text-xs bg-[rgba(114,47,55,0.15)] text-[#D4AF37] border-[rgba(114,47,55,0.3)]">
                        {item.worker_name}
                      </Badge>
                    </div>
                  )}
                  {Object.keys(item.measurements || {}).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {Object.entries(item.measurements).map(([key, val]) => (
                        <div key={key} className="bg-background rounded-sm px-3 py-2 border border-border">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{key}</p>
                          <p className="text-sm font-medium text-foreground">{val || "-"}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {item.notes && (
                    <Button variant="ghost" size="sm" onClick={() => setMessageOpen(true)} className="text-xs gold-text gap-1">
                      <MessageCircle className="w-3 h-3" /> View Notes
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Notes Dialog */}
        <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif">Special Notes</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {slip.items?.map((item, idx) => (
                item.notes && (
                  <div key={idx} className="mb-3 p-3 bg-background rounded-sm border border-border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{item.clothing_type?.replace(/_/g, " ")}</p>
                    <p className="text-sm text-foreground">{item.notes}</p>
                  </div>
                )
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Add New Item */}
        {showAddItem ? (
          <Card className="luxury-card rounded-sm border-dashed border-2 border-[#D4AF37]/30">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-base">Add New Item</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowAddItem(false)} className="h-8 w-8 text-muted-foreground">
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Clothing Type</Label>
                  <Select value={newItem.clothing_type} onValueChange={handleNewItemTypeChange}>
                    <SelectTrigger className="bg-background border-border rounded-sm h-11">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {CLOTHING_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Qty</Label>
                  <div className="flex items-center gap-1 h-11">
                    <Button variant="outline" size="icon" onClick={() => setNewItem({ ...newItem, quantity: Math.max(1, (newItem.quantity || 1) - 1) })} className="h-11 w-11 border-border">
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-12 text-center text-sm">{newItem.quantity}</span>
                    <Button variant="outline" size="icon" onClick={() => setNewItem({ ...newItem, quantity: (newItem.quantity || 1) + 1 })} className="h-11 w-11 border-border">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Assign Worker</Label>
                  <Select value={newItem.worker_id || undefined} onValueChange={handleNewItemWorkerChange}>
                    <SelectTrigger className="bg-background border-border rounded-sm h-11">
                      <SelectValue placeholder="Select worker (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="none">No worker</SelectItem>
                      {workers.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {Object.keys(newItem.measurements).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.keys(newItem.measurements).map(key => (
                    <div key={key} className="space-y-1">
                      <label className="text-xs text-muted-foreground">{key}</label>
                      <Input
                        value={newItem.measurements[key]}
                        onChange={(e) => setNewItem({
                          ...newItem,
                          measurements: { ...newItem.measurements, [key]: e.target.value }
                        })}
                        className="bg-background border-border rounded-sm h-10"
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={addNewItem} disabled={saving} className="btn-maroon rounded-sm w-full">
                {saving ? "Adding..." : "Add Item"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Button variant="outline" onClick={() => setShowAddItem(true)} className="w-full border-dashed border-border rounded-sm gap-2 h-12">
            <Plus className="w-4 h-4" /> Add Another Item
          </Button>
        )}

        {/* Reference Photo */}
        {slip.reference_photo && (
          <Card className="luxury-card rounded-sm">
            <CardHeader className="pb-3"><CardTitle className="font-serif text-base">Reference Photo</CardTitle></CardHeader>
            <CardContent>
              <img src={`${BACKEND_URL}${slip.reference_photo}`} alt="Reference" className="max-w-full sm:max-w-sm rounded-sm border border-border" />
            </CardContent>
          </Card>
        )}

        {/* QR Code */}
        <Card className="luxury-card rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="font-serif text-base">Production QR Code</CardTitle></CardHeader>
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="bg-white p-3 rounded-sm">
              <QRCodeSVG value={`${BACKEND_URL}/api/scan/${slip.id}`} size={100} level="M" fgColor="#1a1a1a" bgColor="#ffffff" />
            </div>
            <div className="space-y-1">
              <p className="text-sm text-foreground font-medium">Scan to log production</p>
              <p className="text-xs text-muted-foreground">Worker: {slip.worker_name || "Unassigned"}</p>
              <p className="text-xs gold-text">Articles: {totalQuantity}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Print View - Exact Format Match */}
      <div className="print-only print-slip">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@800&family=Playfair+Display:wght@700;800&family=Roboto:wght@400;500;700;900&display=swap');

          * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }

          .slip-container { font-family: 'Roboto', 'Segoe UI', Arial, sans-serif; font-size: 10px; color: #111; background: #fff; }

          /* ── SLIP CARD ── */
          .slip { background: #fff; border: 2px solid #5C0A0A; padding: 0; margin-bottom: 0; overflow: hidden; page-break-inside: avoid; break-inside: avoid; }

          /* ── HEADER BAND ── */
          .slip-header { background: #5C0A0A; padding: 5px 10px 4px; }
          .slip-header-top { display: flex; justify-content: space-between; align-items: center; }
          .slip-header-num { text-align: center; padding: 2px 0 1px; border-top: 1px solid rgba(201,148,26,0.3); margin-top: 3px; }
          .slip-header-num .number { font-family: 'Montserrat', 'Segoe UI', Arial, sans-serif; font-size: 28px; line-height: 1.1; font-weight: 800; color: #C9941A; letter-spacing: 1px; }
          .brand { display: flex; align-items: center; gap: 6px; }
          .brand-logo { width: 24px; height: 24px; border-radius: 50%; border: 1.5px solid #C9941A; object-fit: cover; }
          .brand-text h1 { font-family: 'Playfair Display', Georgia, serif; font-size: 11px; font-weight: 800; color: #fff; margin: 0; white-space: nowrap; }
          .brand-text p { font-size: 5px; letter-spacing: 2px; color: #C9941A; text-transform: uppercase; margin: 1px 0 0; font-weight: 700; }
          .slip-date { text-align: right; }
          .slip-date .date { font-size: 7px; color: rgba(255,255,255,0.75); font-weight: 500; white-space: nowrap; }

          /* ── GOLD DIVIDER ── */
          .gold-bar { height: 2px; background: linear-gradient(90deg, #C9941A 0%, #F0CC6A 50%, #C9941A 100%); }

          /* ── BODY PADDING ── */
          .slip-body { padding: 6px 10px; }

          /* ── SUMMARY BODY ── */
          .summary-body { display: flex; gap: 7px; margin-bottom: 6px; }
          .cloth-sample { flex: 0 0 82px; border: 1.5px solid #5C0A0A; padding: 5px; text-align: center; background: #FFF8F0; }
          .cloth-sample-label { font-size: 6px; font-weight: 900; color: #5C0A0A; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px; }
          .cloth-sample-box { width: 60px; height: 52px; margin: 0 auto; border: 1.5px dashed #5C0A0A; display: flex; align-items: center; justify-content: center; }
          .cloth-pin { font-size: 6px; color: #888; margin-top: 3px; line-height: 1.3; font-weight: 500; }

          /* ── CUSTOMER INFO GRID ── */
          .customer-info { flex: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
          .info-row { background: #FFF8F0; border: 1px solid #C9941A; padding: 3px 6px; }
          .info-label { display: block; font-size: 6px; font-weight: 900; color: #5C0A0A; text-transform: uppercase; letter-spacing: 0.5px; }
          .info-value { display: block; font-size: 11px; font-weight: 700; color: #111; margin-top: 1px; line-height: 1.2; }
          .info-value.highlight { color: #5C0A0A; font-weight: 900; }

          /* ── AMOUNTS ROW ── */
          .amounts-row { display: flex; gap: 4px; margin-bottom: 6px; }
          .amount-cell { flex: 1; background: #5C0A0A; padding: 4px 6px; text-align: center; }
          .amount-cell-label { display: block; font-size: 6px; font-weight: 900; color: #C9941A; text-transform: uppercase; letter-spacing: 0.5px; }
          .amount-cell-value { display: block; font-size: 12px; font-weight: 700; color: #fff; margin-top: 2px; min-height: 13px; border-bottom: 1px solid rgba(201,148,26,0.5); padding-bottom: 1px; }

          /* ── ARTICLE LIST ── */
          .article-list { border: 1.5px solid #5C0A0A; margin-bottom: 6px; overflow: hidden; }
          .article-list-table { width: 100%; border-collapse: collapse; }
          .article-list-table thead tr { background: #5C0A0A; }
          .article-list-table thead th { font-size: 7px; font-weight: 900; color: #C9941A; text-transform: uppercase; letter-spacing: 0.8px; padding: 4px 7px; text-align: left; }
          .article-list-table thead th.right { text-align: right; }
          .article-list-table tbody tr:nth-child(even) { background: #FFF8F0; }
          .article-list-table tbody tr:nth-child(odd) { background: #fff; }
          .article-list-table tbody td { font-size: 10px; font-weight: 700; color: #111; padding: 3px 7px; border-bottom: 1px solid #E8D5B0; text-transform: capitalize; }
          .article-list-table tbody td.right { text-align: right; }
          .article-list-table tbody tr:last-child td { border-bottom: none; }

          /* ── AUTH ROW ── */
          .auth-row { display: flex; justify-content: space-around; padding-top: 6px; border-top: 1.5px solid #5C0A0A; margin-top: 3px; }
          .auth-label { font-size: 6.5px; font-weight: 900; color: #5C0A0A; text-transform: uppercase; letter-spacing: 0.8px; padding-top: 10px; border-top: 1.5px solid #C9941A; width: 60px; text-align: center; }

          /* ── CUT LINE ── */
          .cut-line { border: none; border-top: 1.5px dashed #888; margin: 5px 0; }

          /* ── ARTICLE HEADER ── */
          .article-header { display: flex; justify-content: space-between; align-items: center; background: #5C0A0A; color: #fff; padding: 5px 10px; }
          .article-title { font-size: 12px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.8px; color: #fff; }
          .article-qty { font-size: 9px; margin-left: 8px; background: #C9941A; color: #5C0A0A; padding: 1px 6px; font-weight: 900; }
          .article-of { font-size: 8px; color: #C9941A; font-weight: 700; }

          /* ── MEASUREMENTS TABLE ── */
          .meas-table { width: 100%; border-collapse: collapse; background: #fff; border: 1.5px solid #5C0A0A; margin-bottom: 5px; }
          .meas-table thead tr { background: #FFF8F0; }
          .meas-table th { font-size: 7.5px; font-weight: 900; color: #5C0A0A; text-transform: uppercase; letter-spacing: 0.5px; padding: 3px 4px; text-align: center; border-bottom: 2px solid #5C0A0A; border-right: 1px solid #E8D5B0; }
          .meas-table th:last-child { border-right: none; }
          .meas-table td { font-size: 26px; font-weight: 900; color: #111; padding: 5px 4px; text-align: center; border-right: 1px solid #E8D5B0; }
          .meas-table td:last-child { border-right: none; }

          /* ── SUB MEASUREMENTS TABLE ── */
          .meas-sub-table { width: auto; border-collapse: collapse; background: #FFF8F0; border: 1.5px solid #5C0A0A; margin-bottom: 5px; margin-left: auto; }
          .meas-sub-table th { font-size: 7px; font-weight: 900; color: #5C0A0A; text-transform: uppercase; letter-spacing: 0.5px; padding: 3px 10px; text-align: center; border-bottom: 1.5px solid #5C0A0A; border-right: 1px solid #E8D5B0; }
          .meas-sub-table th:last-child { border-right: none; }
          .meas-sub-table td { font-size: 24px; font-weight: 900; color: #111; padding: 4px 10px; text-align: center; border-right: 1px solid #E8D5B0; }
          .meas-sub-table td:last-child { border-right: none; }

          /* ── NOTE ROW ── */
          .note-row { font-size: 24px; line-height: 1.25; color: #111; font-weight: 700; overflow-wrap: anywhere; background: #FFF8F0; border: 1px solid #C9941A; border-left: 3px solid #5C0A0A; padding: 3px 7px; margin-bottom: 5px; }

          /* ── QR FOOTER ── */
          .qr-footer { display: flex; align-items: center; gap: 6px; padding: 4px 0 1px; border-top: 1.5px solid #5C0A0A; margin-top: 3px; }
          .qr-spacer { flex: 1; }
          .delivery-badge { font-size: 8px; font-weight: 900; color: #fff; background: #5C0A0A; padding: 3px 8px; letter-spacing: 0.5px; text-transform: uppercase; }
        `}</style>

        <div className="slip-container">
          {/* SLIP 1 — SUMMARY */}
          <div className="slip">
            <div className="slip-header">
              <div className="slip-header-top">
                <div className="brand">
                  <img src={LOGO_URL} alt="VR" className="brand-logo" />
                  <div className="brand-text">
                    <h1>VR The Darzi Studio</h1>
                    <p>Bespoke Tailoring</p>
                  </div>
                </div>
                <div className="slip-date">
                  <div className="date">{slip.created_at ? format(parseISO(slip.created_at), "dd MMM yy") : ""}</div>
                </div>
              </div>
              <div className="slip-header-num">
                <span className="number">{slip.slip_number}</span>
              </div>
            </div>
            <div className="gold-bar"></div>

            <div className="slip-body">
              <div className="summary-body">
                <div className="cloth-sample">
                  <div className="cloth-sample-label">Cloth Sample</div>
                  <div className="cloth-sample-box">
                    <svg width="38" height="38" viewBox="0 0 36 36" fill="none">
                      <rect x="2" y="2" width="32" height="32" rx="3" stroke="#5C0A0A" strokeWidth="2" strokeDasharray="4 2"></rect>
                      <path d="M8 28 L18 8 L28 28" stroke="#5C0A0A" strokeWidth="2" strokeLinecap="round"></path>
                      <path d="M11 22 L25 22" stroke="#5C0A0A" strokeWidth="1.5" strokeLinecap="round"></path>
                    </svg>
                  </div>
                  <div className="cloth-pin">Pin sample<br/>here</div>
                </div>

                <div className="customer-info">
                  <div className="info-row">
                    <span className="info-label">Name</span>
                    <span className="info-value highlight">{slip.customer?.name || slip.customer_name}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Phone</span>
                    <span className="info-value">{slip.customer?.phone || slip.customer_phone}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Articles</span>
                    <span className="info-value">{totalQuantity}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Delivery</span>
                    <span className="info-value highlight">{slip.delivery_date ? format(parseISO(slip.delivery_date), "dd MMM yyyy") : "N/A"}</span>
                  </div>
                </div>
              </div>

              <div className="amounts-row">
                <div className="amount-cell">
                  <span className="amount-cell-label">Total Amount</span>
                  <span className="amount-cell-value">&nbsp;</span>
                </div>
                <div className="amount-cell">
                  <span className="amount-cell-label">Received</span>
                  <span className="amount-cell-value">&nbsp;</span>
                </div>
                <div className="amount-cell">
                  <span className="amount-cell-label">Left Amount</span>
                  <span className="amount-cell-value">&nbsp;</span>
                </div>
              </div>

              <div className="article-list">
                <table className="article-list-table">
                  <thead>
                    <tr>
                      <th>Article</th>
                      <th className="right">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slip.items?.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ textTransform: 'capitalize' }}>{item.clothing_type?.replace(/_/g, " ")}</td>
                        <td className="right">{item.quantity || 1}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="auth-row">
                <span className="auth-label">Customer</span>
                <span className="auth-label">Authorized</span>
              </div>
            </div>
          </div>

          <hr className="cut-line" />

          {/* Individual Article Slips */}
          {slip.items?.map((item, itemIdx) => {
            const qty = item.quantity || 1;
            const measurements = item.measurements || {};
            const measurementKeys = Object.keys(measurements);
            
            const isLowerBody = ['pant', 'trouser', 'pajama', 'shalwar'].includes(item.clothing_type?.toLowerCase());
            const mainKeys = isLowerBody ? measurementKeys.filter(k => !['Knee', 'Calf', 'U'].includes(k)) : measurementKeys;
            const subKeys = isLowerBody ? measurementKeys.filter(k => ['Knee', 'Calf', 'U'].includes(k)) : [];
            
            return (
              <React.Fragment key={itemIdx}>
                <div className="slip">
                  <div className="slip-header">
                    <div className="slip-header-top">
                      <div className="brand">
                        <img src={LOGO_URL} alt="VR" className="brand-logo" />
                        <div className="brand-text">
                          <h1>VR The Darzi Studio</h1>
                          <p>Bespoke Tailoring</p>
                        </div>
                      </div>
                      <div className="slip-date">
                        <div className="date">{slip.created_at ? format(parseISO(slip.created_at), "dd MMM yy") : ""}</div>
                      </div>
                    </div>
                    <div className="slip-header-num">
                      <span className="number">{slip.slip_number}</span>
                    </div>
                  </div>

                  <div className="article-header">
                    <div>
                      <span className="article-title">{item.clothing_type?.replace(/_/g, " ")}</span>
                      <span className="article-qty">Qty: {qty}</span>
                    </div>
                    <div className="article-of">Article {itemIdx + 1} of {slip.items.length}</div>
                  </div>
                  <div className="gold-bar"></div>

                  <div className="slip-body">
                    {mainKeys.length > 0 && (
                      <table className="meas-table">
                        <thead>
                          <tr>
                            {mainKeys.map(k => <th key={k}>{k}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {mainKeys.map(k => <td key={k}>{measurements[k] || "-"}</td>)}
                          </tr>
                        </tbody>
                      </table>
                    )}

                    {subKeys.length > 0 && (
                      <table className="meas-sub-table">
                        <thead>
                          <tr>
                            {subKeys.map(k => <th key={k}>{k}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {subKeys.map(k => <td key={k}>{measurements[k] || "–"}</td>)}
                          </tr>
                        </tbody>
                      </table>
                    )}

                    {item.notes && (
                      <div className="note-row">Note: {item.notes}</div>
                    )}

                    <div className="qr-footer">
                      <QRCodeSVG value={`${BACKEND_URL}/scan/${slip.id}?article=${itemIdx + 1}`} size={24} level="M" fgColor="#5C0A0A" bgColor="#ffffff" />
                      <span style={{ fontSize: '7px', color: '#888', fontWeight: 700 }}>SCAN</span>
                      <div className="qr-spacer"></div>
                      <div className="delivery-badge">Due: {slip.delivery_date ? format(subDays(parseISO(slip.delivery_date), 2), "dd MMM yy") : "N/A"}</div>
                    </div>
                  </div>
                </div>

                {itemIdx < slip.items.length - 1 && <hr className="cut-line" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      </div>
  );
}
